import { useTranslation } from 'react-i18next'
import { mealLabel } from '../../utils/eatToday'

const round = (v) => Math.round(Number(v) || 0)

function Bar({ value, target }) {
  const share = target > 0 ? Math.min(100, (value / target) * 100) : 0
  const over = target > 0 && value > target
  return (
    <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden" aria-hidden="true">
      <div className={`h-full ${over ? 'bg-amber-500' : 'bg-teal-600'}`} style={{ width: `${share}%` }} />
    </div>
  )
}

/** VH-248: "am I on plan today" in one glance — today's intake against the targets, and what
 *  was logged. Totals are summed from the meals listed, so the two can never disagree. */
export function IntakeSummary({ totals, goal, meals, title, isToday = true }) {
  const { t } = useTranslation()
  const kcalTarget = Number(goal?.target_calories) || 0
  const macros = [
    { key: 'protein', value: totals.protein_g, target: Number(goal?.target_protein_g) || 0 },
    { key: 'carbs', value: totals.carbs_g, target: Number(goal?.carbs_for_date_g ?? goal?.target_carbs_g) || 0 },
    { key: 'fat', value: totals.fat_g, target: Number(goal?.target_fat_g) || 0 },
  ]

  return (
    <section className="bg-surface-container rounded-lg border border-gray-200 p-4 space-y-3" data-testid="eat-intake">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900">{title ?? t('eat.today')}</h2>
        <p className="tabular-nums text-gray-900" data-testid="eat-kcal">
          <span className="text-2xl font-semibold">{round(totals.kcal)}</span>
          {kcalTarget > 0 && <span className="text-sm text-gray-600"> / {round(kcalTarget)}</span>}
          <span className="text-sm text-gray-600"> kcal</span>
        </p>
      </div>
      {kcalTarget > 0 && <Bar value={totals.kcal} target={kcalTarget} />}
      <dl className="grid grid-cols-3 gap-3 text-sm">
        {macros.map((m) => (
          <div key={m.key} className="space-y-1" data-testid={`eat-macro-${m.key}`}>
            <dt className="text-xs text-gray-600">{t(`eat.macro.${m.key}`)}</dt>
            <dd className="tabular-nums text-gray-900">
              {round(m.value)}{m.target > 0 ? ` / ${round(m.target)}` : ''} g
            </dd>
            {m.target > 0 && <Bar value={m.value} target={m.target} />}
          </div>
        ))}
      </dl>
      {!goal && <p className="text-xs text-gray-600">{t('eat.noGoal')}</p>}

      {meals.length === 0 ? (
        <p className="text-sm text-gray-600" data-testid="eat-no-meals">{t(isToday ? 'eat.nothingYet' : 'eat.nothingThatDay')}</p>
      ) : (
        <ul className="divide-y divide-gray-100 text-sm" data-testid="eat-meals">
          {meals.map((meal) => (
            <li key={meal.id} className="flex items-baseline justify-between gap-3 py-1.5">
              <span className="min-w-0 truncate text-gray-800">
                <span className="text-xs text-gray-500 mr-1.5">{t(`nutrition.mealTypes.${meal.meal_type}`, meal.meal_type)}</span>
                {mealLabel(meal)}
              </span>
              <span className="tabular-nums text-gray-600 whitespace-nowrap">{round(meal.total_calories)} kcal</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export default IntakeSummary
